<!--
Author: Alexey Usov (dax@xdax.ru, https://t.me/doubleaxe, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file

Fork rework: factories group by the game's own CoI build-menu
categories by default (scripts/coi-categories.ts); while the user types
in the search field the list switches to live ranked sections ("Name:"
/ "Input recipe:" / "Output recipe:" / "Storage:", with in/out
badges); a plain click on an entry previews the factory in the
right-hand inspector instead of adding it (drag still drags, Add
lives in the inspector).
-->
<script setup lang="ts">
import {GameItemType} from '#types/constants';
import type {GameItem} from '#types/game-data';
import {SelectedClassType, useLeftPanelDragAndDrop, usePointAndClick} from '@/composables/drag-helpers';
import {useInspectorSelection} from '@/composables';
import {injectGameData} from '@/scripts/data';
import {injectFilter} from '@/scripts/filter';
import {injectSettings} from '@/scripts/settings';
import {findStorageMatches} from '@/scripts/storage';
import {COI_CATEGORY_ORDER, COI_ITEM_CATEGORY} from '@/scripts/coi-categories';
import {computed, unref} from 'vue';

const {dragStart} = useLeftPanelDragAndDrop();
const {selectedItem, selectItem} = usePointAndClick();
const {previewItemName, setPreview} = useInspectorSelection();

const filter = injectFilter();
const gameData = injectGameData();
const settings = injectSettings();
const minTier = gameData.gameDescription.minTier;

function computedItemClass(item: GameItem) {
    const classes: string[] = [];
    if(unref(selectedItem)?.isSelected(item))
        classes.push('selected-border');
    if(unref(previewItemName) === item.name)
        classes.push('preview-border');
    return classes;
}

//a plain click previews the factory in the inspector; a drag must not.
//the drag machinery starts on pointerdown, so remember where the press
//began and ignore clicks that end too far from it (drag out-and-back).
let pointerDownPos: {x: number; y: number} | undefined = undefined;
function onEntryPointerDown(event: PointerEvent, item: GameItem) {
    pointerDownPos = {x: event.clientX, y: event.clientY};
    dragStart(event, item);
}
function onEntryClick(event: MouseEvent, item: GameItem) {
    if(pointerDownPos) {
        const moved = Math.max(
            Math.abs(event.clientX - pointerDownPos.x),
            Math.abs(event.clientY - pointerDownPos.y),
        );
        pointerDownPos = undefined;
        if(moved > 6)
            return;
    }
    if(settings.pointAndClickEnabled) {
        //touch place mode keeps its upstream select-then-place flow
        selectItem(SelectedClassType.Item, item);
    } else {
        setPreview(item.name);
    }
}

type IoRelation = 'in' | 'out' | 'both';
type Badge = {glyph: string; title: string; kind: IoRelation};
type DisplayGroup = {
    title: string;
    items: GameItem[];
    badges?: Map<string, Badge>;
};

//first output product of the factory which carries a real physical type;
//workers/maintenance/energy style outputs (Special) do not define the group
function primaryOutputType(item: GameItem): GameItemType | undefined {
    const recipeDictionary = item.recipeDictionary;
    if(!recipeDictionary)
        return undefined;
    for(const recipe of recipeDictionary.recipes) {
        for(const io of recipe.output) {
            const type = io.product?.type;
            if((type !== undefined) && (type !== GameItemType.Unknown) && (type !== GameItemType.Special))
                return type;
        }
    }
    return undefined;
}

function flattenGroups(groups: GameItem[][]): GameItem[] {
    const flat: GameItem[] = [];
    for(const group of groups) {
        if(group)
            flat.push(...group);
    }
    return flat;
}

//does this factory consume / produce the product? mirrors the predicate
//in Filter.buildFilter (exact product + abstract class equivalents)
function ioRelation(item: GameItem, product: GameItem): IoRelation | undefined {
    const recipeDictionary = item.recipeDictionary;
    if(!recipeDictionary)
        return undefined;
    const abstractType = product.isAbstractClassItem ? product.type : undefined;
    const relativeAbstract = product.type ? gameData.gameAbstractItems.get(product.type) : undefined;
    const consumes = recipeDictionary.recipesByInputMap.has(product.name)
        || ((abstractType !== undefined) && recipeDictionary.hasInputTypes.has(abstractType))
        || (!!relativeAbstract && recipeDictionary.recipesByInputMap.has(relativeAbstract.name));
    const produces = recipeDictionary.recipesByOutputMap.has(product.name)
        || ((abstractType !== undefined) && recipeDictionary.hasOutputTypes.has(abstractType))
        || (!!relativeAbstract && recipeDictionary.recipesByOutputMap.has(relativeAbstract.name));
    if(consumes && produces)
        return 'both';
    if(consumes)
        return 'in';
    if(produces)
        return 'out';
    return undefined;
}

//live ranked sections while plain-typing in the search field. An
//explicit filter picked from the suggestions (filter.key) takes over
//the list instead, and clearing the search restores the grouped list.
const searchGroups = computed<DisplayGroup[] | undefined>(() => {
    if(filter.key)
        return undefined;
    const query = filter.liveQuery.trim().toLowerCase();
    if(!query)
        return undefined;
    const flat = flattenGroups(filter.buildFilter());

    const nameMatches = flat
        .map((item) => {
            const label = item.lowerLabel;
            const name = item.name.toLowerCase();
            let rank: number;
            if(label.startsWith(query))
                rank = 0;
            else if(name.startsWith(query))
                rank = 1;
            else if(label.indexOf(query) > -1)
                rank = 2;
            else if(name.indexOf(query) > -1)
                rank = 3;
            else
                return undefined;
            return {item, rank};
        })
        .filter((entry): entry is {item: GameItem; rank: number} => !!entry)
        .sort((a, b) => (a.rank - b.rank) || a.item.label.localeCompare(b.item.label))
        .map(({item}) => item);
    const inNameMatches = new Set(nameMatches.map((item) => item.name));

    //best product match: label prefix beats substring, shortest label
    //wins ties, then A-Z (product internal names are packaged keys, so
    //only labels are meaningful here)
    let product: GameItem | undefined = undefined;
    let productRank = Number.POSITIVE_INFINITY;
    for(const item of gameData.gameItemsArray) {
        if(item.recipeDictionary)
            continue;
        const label = item.lowerLabel;
        let rank: number;
        if(label.startsWith(query))
            rank = 0;
        else if(label.indexOf(query) > -1)
            rank = 1;
        else
            continue;
        if((rank < productRank)
            || ((rank === productRank) && product && ((label.length < product.lowerLabel.length)
                || ((label.length === product.lowerLabel.length) && (label < product.lowerLabel))))
        ) {
            product = item;
            productRank = rank;
        }
    }

    //fork change: four groups, framed as "matches on" — the factory's
    //own name, the recipes that take the matched product in, the
    //recipes that put it out, and the storage that can hold it. Name
    //matches are excluded from the recipe/storage groups; a factory
    //doing both directions appears in both recipe groups.
    const groups: DisplayGroup[] = [];
    if(nameMatches.length)
        groups.push({title: 'Name:', items: nameMatches});
    if(product) {
        const consumers: GameItem[] = [];
        const producers: GameItem[] = [];
        const inBadges = new Map<string, Badge>();
        const outBadges = new Map<string, Badge>();
        for(const item of flat) {
            if(inNameMatches.has(item.name))
                continue;
            const relation = ioRelation(item, product);
            if(!relation)
                continue;
            if((relation === 'in') || (relation === 'both')) {
                consumers.push(item);
                inBadges.set(item.name, {glyph: '↓', title: `Consumes ${product.label}`, kind: 'in'});
            }
            if((relation === 'out') || (relation === 'both')) {
                producers.push(item);
                outBadges.set(item.name, {glyph: '↑', title: `Produces ${product.label}`, kind: 'out'});
            }
        }
        consumers.sort((a, b) => a.label.localeCompare(b.label));
        producers.sort((a, b) => a.label.localeCompare(b.label));
        if(consumers.length)
            groups.push({title: 'Input recipe:', items: consumers, badges: inBadges});
        if(producers.length)
            groups.push({title: 'Output recipe:', items: producers, badges: outBadges});
        const storageMatches = findStorageMatches(gameData, product, 'any')
            .filter((match) => !inNameMatches.has(match.factory.name));
        if(storageMatches.length) {
            const badges = new Map<string, Badge>();
            const productLabel = product.label;
            const items = storageMatches.map((match) => {
                const kind: IoRelation = (match.acceptsInput && match.producesOutput)
                    ? 'both'
                    : (match.acceptsInput ? 'in' : 'out');
                badges.set(match.factory.name, {
                    glyph: (kind === 'both') ? '⇅' : (kind === 'in') ? '↓' : '↑',
                    title: `Stores ${productLabel}`,
                    kind,
                });
                return match.factory;
            });
            items.sort((a, b) => a.label.localeCompare(b.label));
            groups.push({title: 'Storage:', items, badges});
        }
    }
    return groups;
});

const displayGroups = computed<DisplayGroup[]>(() => {
    const groups = filter.buildFilter();
    if(filter.groupMode === 'tier') {
        //buildFilter groups are indexed by (tier - minTier) and may be sparse
        const result: DisplayGroup[] = [];
        for(let i = 0; i < groups.length; i++) {
            const items = groups[i];
            if(items && items.length)
                result.push({title: `Tier ${i + minTier}`, items});
        }
        return result;
    }
    const flat = flattenGroups(groups);
    if(filter.groupMode === 'category') {
        //the game's own build-menu categories, in the game's order;
        //factories without a known category land in "Other", last
        const byCategory = new Map<string, GameItem[]>();
        for(const item of flat) {
            const category = COI_ITEM_CATEGORY[item.name] || 'Other';
            let array = byCategory.get(category);
            if(!array) {
                array = [];
                byCategory.set(category, array);
            }
            array.push(item);
        }
        const result: DisplayGroup[] = [];
        for(const category of [...COI_CATEGORY_ORDER, 'Other']) {
            const items = byCategory.get(category);
            if(items && items.length)
                result.push({title: category, items});
        }
        return result;
    }
    if(filter.groupMode === 'output') {
        const byType = new Map<GameItemType | undefined, GameItem[]>();
        for(const item of flat) {
            const type = primaryOutputType(item);
            let array = byType.get(type);
            if(!array) {
                array = [];
                byType.set(type, array);
            }
            array.push(item);
        }
        const types = [...byType.keys()].sort((a, b) => {
            //enum order, untyped factories ("Other") always last
            if(a === undefined)
                return 1;
            if(b === undefined)
                return -1;
            return a - b;
        });
        return types.map((type) => ({
            title: (type === undefined) ? 'Other' : GameItemType[type],
            items: byType.get(type) || [],
        }));
    }
    return [{title: '', items: flat}];
});

const renderGroups = computed<DisplayGroup[]>(() => unref(searchGroups) ?? unref(displayGroups));
const isEmpty = computed(() => !unref(renderGroups).some((group) => group.items.length));
</script>

<template>
    <div>
        <icon-list-filter />
        <div class="icon-div-parent bg-window-idle">
            <optimized-tooltip>
                <template v-for="(group, _index1) in renderGroups" :key="_index1">
                    <div v-if="group.title" class="group-heading bg-window-idle">
                        {{ group.title }}
                    </div>
                    <template v-if="(settings.leftPanelDisplayMode === 'icons')">
                        <span
                            v-for="item in group.items"
                            :key="item.name"
                            class="entry-icon-wrap"
                        >
                            <icon-component
                                class="rounded icon-div hover-elevation"
                                :class="computedItemClass(item)"
                                :image="item.image"
                                :data-tooltip="item.label"
                                @pointerdown.left="onEntryPointerDown($event, item)"
                                @click="onEntryClick($event, item)"
                            />
                            <span
                                v-if="group.badges?.get(item.name)"
                                class="io-badge io-badge-overlay"
                                :class="`io-badge-${group.badges.get(item.name)?.kind}`"
                                :title="group.badges.get(item.name)?.title"
                            >{{ group.badges.get(item.name)?.glyph }}</span>
                        </span>
                    </template>
                    <template v-else>
                        <div
                            v-for="item in group.items"
                            :key="item.name"
                            class="item-row rounded hover-elevation"
                            :class="computedItemClass(item)"
                            :data-tooltip="item.label"
                            @pointerdown.left="onEntryPointerDown($event, item)"
                            @click="onEntryClick($event, item)"
                        >
                            <icon-component
                                v-if="(settings.leftPanelDisplayMode === 'both')"
                                class="icon-div"
                                :image="item.image"
                            />
                            <span class="item-name">{{ item.label }}</span>
                            <span
                                v-if="group.badges?.get(item.name)"
                                class="io-badge"
                                :class="`io-badge-${group.badges.get(item.name)?.kind}`"
                                :title="group.badges.get(item.name)?.title"
                            >{{ group.badges.get(item.name)?.glyph }}</span>
                        </div>
                    </template>
                </template>
                <div v-if="isEmpty" class="empty-note">
                    No factories match
                </div>
            </optimized-tooltip>
        </div>
    </div>
</template>

<style scoped>
.icon-div-parent {
    line-height: 0px;
    font-size: 0px;
    user-select: none;
}
.icon-div {
    display: inline-block;
}
.icon-draggable {
    position: fixed;
    z-index: 5000;
}
.group-heading {
    position: sticky;
    top: 0px;
    z-index: 1;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.04em;
    padding: 4px 8px 2px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.35);
}
.item-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 6px;
    cursor: pointer;
    line-height: normal;
}
.item-name {
    flex: 1 1 auto;
    min-width: 0px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    line-height: 1.3;
}
/* fork addition: entry currently previewed in the inspector */
.preview-border {
    box-shadow: 0 0 0 2px #3949ab, 0 0 8px rgba(57, 73, 171, 0.5);
}
/* fork addition: why-is-this-listed badge in live search results */
.io-badge {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    color: #fff;
    border-radius: 4px;
    padding: 2px 4px;
}
.io-badge-in {
    background-color: #00897b;
}
.io-badge-out {
    background-color: #ef6c00;
}
.io-badge-both {
    background-color: #5e35b1;
}
.entry-icon-wrap {
    position: relative;
    display: inline-block;
    line-height: 0px;
}
.io-badge-overlay {
    position: absolute;
    right: 0px;
    bottom: 0px;
    font-size: 9px;
    padding: 1px 3px;
    pointer-events: none;
}
.empty-note {
    font-size: 13px;
    line-height: 1.4;
    padding: 8px;
}
</style>
